const FOCUSABLE_SELECTORS = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
];

/**
 * Gets all keyboard focusable elements within a container
 */
export function getKeyboardFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS.join(','))).filter(
    (el) => {
      const style = window.getComputedStyle(el);
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        !el.hasAttribute('disabled') &&
        !el.getAttribute('aria-hidden')
      );
    },
  );
}

export function getFirstFocusableElement(container: HTMLElement): HTMLElement | null {
  const focusableElements = getKeyboardFocusableElements(container);
  return focusableElements.length > 0 ? focusableElements[0] : null;
}

export function getLastFocusableElement(container: HTMLElement): HTMLElement | null {
  const focusableElements = getKeyboardFocusableElements(container);
  return focusableElements.length > 0 ? focusableElements[focusableElements.length - 1] : null;
}

/**
 * Focus trap handler for asset containers
 */
export function handleAssetContainerFocusTrap(
  e: KeyboardEvent,
  assetContainer: HTMLElement,
  textContainer: HTMLElement,
  nextButton: HTMLButtonElement,
  prevButton: HTMLButtonElement,
  isMobile: boolean,
) {
  if (e.key !== 'Tab' || e.shiftKey) return;

  const lastFocusableAsset = getLastFocusableElement(assetContainer);
  let hasMovedFocus = false;

  const moveFocus = (target: HTMLElement | null) => {
    if (!hasMovedFocus && target) {
      hasMovedFocus = true;
      target.focus({ preventScroll: true });
      setTimeout(() => {
        hasMovedFocus = false;
      }, 50);
    }
  };

  const getMobileTargetAfterAsset = () => {
    const firstFocusableText = getFirstFocusableElement(textContainer);
    if (firstFocusableText) {
      return firstFocusableText;
    }
    return !nextButton.disabled ? nextButton : prevButton;
  };

  const getDesktopTargetAfterAsset = () => {
    return !nextButton.disabled ? nextButton : prevButton;
  };

  const shouldTrapFocus = !lastFocusableAsset || document.activeElement === lastFocusableAsset;

  if (shouldTrapFocus) {
    e.preventDefault();
    const target = isMobile ? getMobileTargetAfterAsset() : getDesktopTargetAfterAsset();
    moveFocus(target);
  }
}

export function handleTextContainerFocusTrap(
  e: KeyboardEvent,
  textContainer: HTMLElement,
  nextButton: HTMLButtonElement,
  prevButton: HTMLButtonElement,
) {
  if (e.key !== 'Tab' || e.shiftKey) return;

  const lastFocusable = getLastFocusableElement(textContainer);

  const shouldTrapFocus = !lastFocusable || document.activeElement === lastFocusable;

  if (shouldTrapFocus) {
    e.preventDefault();
    const target = !nextButton.disabled ? nextButton : prevButton;
    target.focus({ preventScroll: true });
  }
}

// moves focus between elements when a specific key is pressed (arrow navigation)
export function navigate(
  container: HTMLElement,
  key: string,
  forwardKey: string,
  backwardKey: string,
) {
  const items = Array.from(container.querySelectorAll('a, button')).filter(
    (el) => (el as HTMLElement).offsetParent !== null,
  ) as HTMLElement[];

  const active = document.activeElement as HTMLElement;
  const index = items.indexOf(active);
  if (index === -1) return;

  if (key === forwardKey) {
    const next = (index + 1) % items.length;
    items[next].focus();
  } else if (key === backwardKey) {
    const prev = (index - 1 + items.length) % items.length;
    items[prev].focus();
  }
}

export function attachHorizontalNavigation(container: HTMLElement) {
  const handler = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      navigate(container, e.key, 'ArrowRight', 'ArrowLeft');
    }
  };
  container.addEventListener('keydown', handler);
  return () => container.removeEventListener('keydown', handler);
}
